import { useRouter } from 'next/router'

import { indexes } from '@mab-common/utils'

import type {
  MsearchRequest,
  MsearchRequestItem,
  QueryDslQueryContainer,
} from '@elastic/elasticsearch/lib/api/types'

import useSWRInfinite from 'swr/infinite'

import type { filtersType } from '~/components/blocks/ArtworksList/ArtworksList'
import {
  buildAggsCountField,
  buildArtistFacet,
  buildElasticAggsToBuckets,
  buildElasticAggsToHits,
  buildElasticResultSourceFields,
  buildElasticSearchTerm,
  buildElasticSortFields,
  buildElasticToHits,
  buildFormatFacet,
  buildGenreFacet,
  buildMappedElasticHits,
  buildPriceFacet,
} from '~/services/elastic/builder'
import { elasticClientFetcher } from '~/services/elastic/client'
import { getRouterPath } from '~/utils/getRouterPath'
import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes

export default function useArtworks(
  searchTerm: string,
  queriedId?: string,
  type?: 'artist' | 'collection' | 'genre',
  filters?: filtersType,
  fallbackData?: any,
  pageSize = 12
) {
  const router = useRouter()
  const currentPathname = getRouterPath(router)
  const isTradingFloor =
    currentPathname.endsWith('/trading-floor') ||
    currentPathname === '/trading-floor' ||
    currentPathname.endsWith('/salle-des-marches') ||
    currentPathname === '/salle-des-marches'

  const indexName =
    filters?.tradingFloor || isTradingFloor
      ? EsIndexTypes.TICKETS
      : EsIndexTypes.ARTWORKS
  const recordType =
    indexName === EsIndexTypes.TICKETS
      ? RecordTypes.TICKETS
      : RecordTypes.ARTWORK

  const getQuery = (pageIndex: number, previousPageData: any) => {
    if (previousPageData && !previousPageData.length) return null // reached the end

    const pageFrom = pageIndex * pageSize
    const aggsRandSize = 1000

    const genreFilters: QueryDslQueryContainer[] =
      filters && filters['genres'].length > 0
        ? [
            {
              terms: {
                'genres._id.keyword': filters['genres'],
              },
            },
          ]
        : []

    const formatFilters: QueryDslQueryContainer[] =
      filters && filters['formats'].length > 0
        ? [
            {
              terms: {
                'format._id.keyword': filters['formats'],
              },
            },
          ]
        : []

    const artistFilters: QueryDslQueryContainer[] =
      filters && filters['artists'].length > 0
        ? [
            {
              terms: {
                artistId: filters['artists'],
              },
            },
          ]
        : []

    const priceFilters: QueryDslQueryContainer[] =
      filters && filters['prices'].length > 1
        ? [
            {
              range: {
                listPriceMin: {
                  gte: +filters['prices']?.[0],
                  lte: +filters['prices']?.[1],
                },
              },
            },
          ]
        : []

    const quriedFilters: QueryDslQueryContainer[] =
      type && queriedId
        ? [
            {
              term: {
                [type === 'genre'
                  ? `genres._id.keyword`
                  : type === 'artist'
                    ? `artistId`
                    : 'collectionId.keyword']: queriedId,
              },
            },
          ]
        : []

    let filterQueries: QueryDslQueryContainer[] = [
      {
        term: {
          type: 'artwork',
        },
      },
      ...quriedFilters,
    ]
    let mustNotQueries: QueryDslQueryContainer[] = [
      {
        term: {
          isVisible: false,
        },
      },
    ]

    const mustQueries: any = buildElasticSearchTerm(recordType, searchTerm)

    if (filters?.tradingFloor) {
      mustNotQueries = []
      filterQueries = [
        {
          term: {
            ticketStatus: filters.tradingFloor,
          },
        },
        ...quriedFilters,
      ]
    }
    if (!filters?.tradingFloor && isTradingFloor) {
      mustNotQueries = []
      filterQueries = [...quriedFilters]
    }

    const base: MsearchRequestItem = {
      sort: buildElasticSortFields(recordType, Boolean(type === 'artist')),
      _source: buildElasticResultSourceFields(recordType),
    }
    let extraAggs: MsearchRequestItem[] = []
    if (indexName === EsIndexTypes.ARTWORKS) {
      extraAggs = [
        { index: indexName },
        {
          ...base,
          size: 0,
          query: {
            bool: {
              must: mustQueries,
              must_not: mustNotQueries,
              // this is for formatFacet
              filter: [
                ...filterQueries,
                ...genreFilters,
                ...artistFilters,
                ...priceFilters,
              ],
            },
          },
          aggs: {
            [buildAggsCountField('format')]: {
              cardinality: { field: 'format._id.keyword' },
            },
            format: {
              terms: { field: 'format._id.keyword', size: aggsRandSize },
              aggs: {
                docs: {
                  top_hits: {
                    size: 1,
                    _source: ['format._id', 'format.name', 'format.slug'],
                  },
                },
              },
            },
          },
        },
        { index: indexName },
        {
          ...base,
          size: 0,
          query: {
            bool: {
              must: mustQueries,
              must_not: mustNotQueries,
              // this is for genreFacet
              filter: [
                ...filterQueries,
                ...formatFilters,
                ...artistFilters,
                ...priceFilters,
              ],
            },
          },
          aggs: {
            [buildAggsCountField('genre')]: {
              cardinality: { field: 'genres._id.keyword' },
            },
            genre: {
              terms: { field: 'genres._id.keyword', size: aggsRandSize },
              aggs: {
                docs: {
                  top_hits: {
                    size: 1,
                    _source: ['genres._id', 'genres.name', 'genres.slug'],
                  },
                },
              },
            },
          },
        },
      ]
    }
    return ((): MsearchRequest => {
      return {
        index: indexName,
        searches: [
          {},
          {
            ...base,
            from: pageFrom,
            size: pageSize,
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                // this is for all combined
                filter: [
                  ...filterQueries,
                  ...formatFilters,
                  ...genreFilters,
                  ...artistFilters,
                  ...priceFilters,
                ],
              },
            },
          },
          { index: indexName },
          {
            ...base,
            size: 0,
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                // this is for artistFacet
                filter: [
                  ...filterQueries,
                  ...formatFilters,
                  ...genreFilters,
                  ...priceFilters,
                ],
              },
            },
            aggs: {
              [buildAggsCountField('artist')]: {
                cardinality: { field: 'artistId' },
              },
              artist: {
                terms: { field: 'artistId', size: aggsRandSize },
                aggs: {
                  docs: {
                    top_hits: {
                      size: 1,
                      _source: ['artist', 'artistId', 'artistSlug'],
                    },
                  },
                },
              },
            },
          },
          { index: indexName },
          {
            ...base,
            size: 0,
            query: {
              bool: {
                must: mustQueries,
                must_not: mustNotQueries,
                // this is for priceFacet
                filter: [
                  ...filterQueries,
                  ...formatFilters,
                  ...genreFilters,
                  ...artistFilters,
                  {
                    term: {
                      poa: false,
                    },
                  },
                ],
              },
            },
            aggs: {
              price: {
                terms: { field: 'listPriceMin', size: aggsRandSize },
              },
            },
          },
          ...extraAggs,
        ],
      }
    })()
  }

  const { data, error, mutate, size, setSize, isValidating } = useSWRInfinite(
    getQuery,
    async (props: MsearchRequest) => {
      const d = await elasticClientFetcher(props)

      return [
        buildMappedElasticHits(buildElasticToHits(recordType, d?.[0]))?.[0],
        {
          artist:
            buildElasticAggsToHits(d?.[1]).map(buildArtistFacet)?.[0]?.facets ||
            [],
          price:
            buildElasticAggsToBuckets(d?.[2]).map(buildPriceFacet)?.[0]
              ?.facets || [],
          format:
            buildElasticAggsToHits(d?.[3]).map(buildFormatFacet)?.[0]?.facets ||
            [],
          genre:
            buildElasticAggsToHits(d?.[4]).map(buildGenreFacet)?.[0]?.facets ||
            [],
        },
      ]
    },
    {
      revalidateOnFocus: false,
      fallbackData: fallbackData,
      revalidateFirstPage: false,
    }
  )

  return {
    data: data?.flatMap(d => d?.[0]).filter(Boolean),
    facets: data?.[0]?.[1],
    error,
    mutate,
    size,
    setSize,
    isValidating,
    tradingView: isTradingFloor,
  }
}
